import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtIDR, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import {
  Package, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, Plus, Pencil, History, Beaker, ClipboardList, Trash2,
} from "lucide-react";

const CATEGORIES = [
  { v: "bhp", l: "BHP Treatment" },
  { v: "medication", l: "Obat & Injeksi" },
  { v: "retail", l: "Produk Retail" },
  { v: "equipment", l: "Alat & Equipment" },
  { v: "other", l: "Lainnya" },
];

export default function Inventory() {
  const { branchId, branches } = useBranch();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [movementItem, setMovementItem] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveType, setMoveType] = useState("in");
  const [moveQty, setMoveQty] = useState("");
  const [moveNote, setMoveNote] = useState("");
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    branch_id: "", name: "", category: "bhp", sku: "", unit: "pcs",
    quantity: 0, min_stock: 0, unit_cost: 0,
  });
  const [moveExtra, setMoveExtra] = useState({ po_number: "", invoice_number: "", due_date: "" });

  // Treatment recipe (BOM)
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [treatmentsList, setTreatmentsList] = useState([]);
  const [recipeTreatmentId, setRecipeTreatmentId] = useState("");
  const [recipeTreatmentQ, setRecipeTreatmentQ] = useState("");
  const [recipeItems, setRecipeItems] = useState([]);
  const [recipeSaving, setRecipeSaving] = useState(false);

  // Stock opname (selisih)
  const [opnameOpen, setOpnameOpen] = useState(false);
  const [opnameRows, setOpnameRows] = useState({});
  const [opnameSaving, setOpnameSaving] = useState(false);

  const isCoord = user?.role === "owner" || user?.role === "manager" || user?.is_coordinator;
  const canEditQty = user?.role === "owner" || user?.role === "manager" || user?.role === "therapist" || user?.role === "fo_cs";
  const canCorrectOpname = user?.role === "owner" || user?.role === "manager";

  const refresh = () => {
    api.get("/inventory", { params: { branch_id: branchId } }).then((r) => setItems(r.data));
  };

  useEffect(() => { refresh(); }, [branchId]);

  const filtered = useMemo(() => {
    let r = items;
    if (tab === "low") r = r.filter((i) => i.is_low);
    else if (tab !== "all") r = r.filter((i) => i.category === tab);
    if (search) r = r.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()) || (i.sku || "").toLowerCase().includes(search.toLowerCase()));
    return r;
  }, [items, tab, search]);

  const counts = useMemo(() => {
    return {
      all: items.length,
      low: items.filter((i) => i.is_low).length,
      bhp: items.filter((i) => i.category === "bhp").length,
      medication: items.filter((i) => i.category === "medication").length,
      retail: items.filter((i) => i.category === "retail").length,
      equipment: items.filter((i) => i.category === "equipment").length,
    };
  }, [items]);

  const totalValue = useMemo(() => items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_cost || 0), 0), [items]);

  const openMoveDialog = (item, type) => {
    setMovementItem(item);
    setMoveType(type);
    setMoveQty("");
    setMoveNote("");
    setMoveExtra({ po_number: "", invoice_number: "", due_date: "" });
    setMoveDialogOpen(true);
  };

  const submitMovement = async (e) => {
    e?.preventDefault?.();
    const q = Number(moveQty);
    if (isNaN(q) || q < 0) { toast.error("Quantity invalid"); return; }
    try {
      const payload = {
        inventory_id: movementItem.id,
        movement_type: moveType,
        quantity: q,
        note: moveNote,
      };
      if (moveType === "in") {
        payload.po_number = moveExtra.po_number || null;
        payload.invoice_number = moveExtra.invoice_number || null;
        payload.due_date = moveExtra.due_date || null;
      }
      await api.post("/inventory/movements", payload);
      toast.success(`Movement ${moveType} tercatat`);
      setMoveDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  // ---- Treatment recipe ----
  const openRecipe = async () => {
    setRecipeTreatmentId("");
    setRecipeItems([]);
    setRecipeOpen(true);
    const { data } = await api.get("/treatments");
    setTreatmentsList(data);
  };

  const loadRecipe = async (tid) => {
    setRecipeTreatmentId(tid);
    if (!tid) { setRecipeItems([]); return; }
    const { data } = await api.get(`/treatments/${tid}/recipe`);
    setRecipeItems((data.items || []).map((i) => ({ inventory_id: i.inventory_id, quantity: i.quantity })));
  };

  const addRecipeRow = () => setRecipeItems([...recipeItems, { inventory_id: "", quantity: 1 }]);
  const removeRecipeRow = (idx) => setRecipeItems(recipeItems.filter((_, i) => i !== idx));
  const updateRecipeRow = (idx, patch) => setRecipeItems(recipeItems.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const saveRecipe = async () => {
    if (!recipeTreatmentId) { toast.error("Pilih treatment dulu"); return; }
    const clean = recipeItems.filter((r) => r.inventory_id && Number(r.quantity) > 0)
      .map((r) => ({ inventory_id: r.inventory_id, quantity: Number(r.quantity) }));
    setRecipeSaving(true);
    try {
      await api.put(`/treatments/${recipeTreatmentId}/recipe`, { items: clean });
      toast.success(`Resep tersimpan (${clean.length} bahan)`);
      setRecipeOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally {
      setRecipeSaving(false);
    }
  };

  // ---- Stock opname (selisih) ----
  const openOpname = () => {
    setOpnameRows({});
    setOpnameOpen(true);
  };

  const saveOpname = async () => {
    const entries = Object.entries(opnameRows).filter(([, v]) => v !== "" && v != null);
    if (entries.length === 0) { toast.error("Isi minimal 1 stok fisik"); return; }
    setOpnameSaving(true);
    try {
      for (const [id, phys] of entries) {
        await api.post("/inventory/movements", {
          inventory_id: id,
          movement_type: "opname",
          quantity: Number(phys),
          note: "Stok opname (hitung fisik)",
        });
      }
      toast.success(`Opname tersimpan untuk ${entries.length} item`);
      setOpnameOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally {
      setOpnameSaving(false);
    }
  };

  const showHistory = async (item) => {
    setMovementItem(item);
    const { data } = await api.get("/inventory/movements", { params: { inventory_id: item.id } });
    setMovements(data);
    setMovementsOpen(true);
  };

  const openCreateItem = () => {
    setEditing(null);
    setForm({ branch_id: branches[0]?.id || "", name: "", category: "bhp", sku: "", unit: "pcs", quantity: 0, min_stock: 0, unit_cost: 0 });
    setItemDialogOpen(true);
  };

  const openEditItem = (item) => {
    setEditing(item);
    setForm({
      branch_id: item.branch_id, name: item.name, category: item.category,
      sku: item.sku || "", unit: item.unit, quantity: item.quantity,
      min_stock: item.min_stock, unit_cost: item.unit_cost,
    });
    setItemDialogOpen(true);
  };

  const submitItem = async (e) => {
    e?.preventDefault?.();
    try {
      if (editing) {
        const { quantity, branch_id, ...patch } = form;
        await api.patch(`/inventory/${editing.id}`, {
          ...patch,
          min_stock: Number(form.min_stock), unit_cost: Number(form.unit_cost),
        });
        toast.success("Item diperbarui");
      } else {
        await api.post("/inventory", {
          ...form,
          quantity: Number(form.quantity),
          min_stock: Number(form.min_stock),
          unit_cost: Number(form.unit_cost),
        });
        toast.success("Item ditambahkan");
      }
      setItemDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">
            Inventory {user?.is_coordinator && <Badge className="ml-2 align-middle">Koordinator</Badge>}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Total nilai stok: <span className="font-medium text-foreground">{fmtIDR(totalValue)}</span> • {items.length} SKU • {counts.low} stok rendah
          </p>
        </div>
        {isCoord && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="recipe-btn" onClick={openRecipe}>
              <Beaker className="mr-1.5 h-4 w-4" /> Resep Treatment
            </Button>
            <Button variant="outline" data-testid="opname-bulk-btn" onClick={openOpname}>
              <ClipboardList className="mr-1.5 h-4 w-4" /> Stok Opname
            </Button>
            <Button data-testid="add-item-btn" onClick={openCreateItem}>
              <Plus className="mr-1.5 h-4 w-4" /> Tambah Item
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="inventory-tabs">
          <TabsTrigger value="all">Semua ({counts.all})</TabsTrigger>
          <TabsTrigger value="low" data-testid="tab-low" className="text-rose-700">
            <AlertTriangle className="mr-1 h-3 w-3" /> Stok Rendah ({counts.low})
          </TabsTrigger>
          <TabsTrigger value="bhp">BHP ({counts.bhp})</TabsTrigger>
          <TabsTrigger value="medication">Obat ({counts.medication})</TabsTrigger>
          <TabsTrigger value="retail">Retail ({counts.retail})</TabsTrigger>
          <TabsTrigger value="equipment">Equipment ({counts.equipment})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Package className="h-4 w-4" /> Daftar Stok ({filtered.length})
          </CardTitle>
          <Input
            data-testid="inventory-search"
            placeholder="Cari nama / SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Cabang</TableHead>
                <TableHead className="text-right">Stok</TableHead>
                <TableHead className="text-right">Min</TableHead>
                <TableHead className="text-right">Harga</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.slice(0, 300).map((i) => {
                const br = branches.find((b) => b.id === i.branch_id);
                return (
                  <TableRow key={i.id} data-testid={`inv-${i.id}`} className={i.is_low ? "bg-rose-50/30" : ""}>
                    <TableCell className="font-medium text-sm">{i.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.sku}</TableCell>
                    <TableCell className="text-xs capitalize">{CATEGORIES.find((c) => c.v === i.category)?.l}</TableCell>
                    <TableCell className="text-xs">{br?.name?.replace("MyClinic ", "")}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-medium ${i.is_low ? "text-rose-700" : ""}`}>
                        {i.quantity} {i.unit}
                      </span>
                      {i.is_low && <AlertTriangle className="inline h-3 w-3 ml-1 text-rose-600" />}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{i.min_stock} {i.unit}</TableCell>
                    <TableCell className="text-right text-xs">{fmtIDR(i.unit_cost)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEditQty && (
                          <Button size="sm" variant="outline" data-testid={`stock-in-${i.id}`} onClick={() => openMoveDialog(i, "in")} title="Tambah stok">
                            <ArrowDownToLine className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canEditQty && (
                          <Button size="sm" variant="outline" data-testid={`stock-out-${i.id}`} onClick={() => openMoveDialog(i, "out")} title="Pakai stok">
                            <ArrowUpFromLine className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isCoord && (
                          <Button size="sm" variant="outline" data-testid={`opname-${i.id}`} onClick={() => openMoveDialog(i, "opname")} title="Stok opname">
                            <ClipboardCheck className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" data-testid={`history-${i.id}`} onClick={() => showHistory(i)} title="Riwayat">
                          <History className="h-3.5 w-3.5" />
                        </Button>
                        {isCoord && (
                          <Button size="sm" variant="ghost" data-testid={`edit-item-${i.id}`} onClick={() => openEditItem(i)} title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Movement dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent data-testid="movement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl capitalize">
              {moveType === "in" ? "Tambah Stok" : moveType === "out" ? "Pakai Stok / Out" : moveType === "opname" ? "Stok Opname" : "Adjustment"}
            </DialogTitle>
            <DialogDescription>
              Item: <span className="font-medium text-foreground">{movementItem?.name}</span>
              <br />
              Stok saat ini: {movementItem?.quantity} {movementItem?.unit}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMovement} className="space-y-4">
            <div>
              <Label>{moveType === "opname" ? "Stok Fisik Aktual" : `Quantity (${moveType})`} *</Label>
              <Input type="number" min="0" step="1" data-testid="move-qty-input" value={moveQty} onChange={(e) => setMoveQty(e.target.value)} required />
            </div>
            {moveType === "in" && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-secondary/30 p-3">
                <div>
                  <Label className="text-xs">No. PO</Label>
                  <Input data-testid="move-po-input" value={moveExtra.po_number} onChange={(e) => setMoveExtra({ ...moveExtra, po_number: e.target.value })} placeholder="PO-2026-001" />
                </div>
                <div>
                  <Label className="text-xs">No. Invoice</Label>
                  <Input data-testid="move-invoice-input" value={moveExtra.invoice_number} onChange={(e) => setMoveExtra({ ...moveExtra, invoice_number: e.target.value })} placeholder="INV-SUP-123" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Tgl Jatuh Tempo</Label>
                  <Input type="date" data-testid="move-due-input" value={moveExtra.due_date} onChange={(e) => setMoveExtra({ ...moveExtra, due_date: e.target.value })} />
                </div>
              </div>
            )}
            <div>
              <Label>Catatan</Label>
              <Input data-testid="move-note-input" value={moveNote} onChange={(e) => setMoveNote(e.target.value)} placeholder="cth. supplier, batch, hasil opname fisik" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMoveDialogOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="move-save-btn">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Movement history dialog */}
      <Dialog open={movementsOpen} onOpenChange={setMovementsOpen}>
        <DialogContent className="max-w-2xl" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Riwayat Stok — {movementItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Waktu</TableHead>
                  <TableHead>Tipe</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Sebelum → Sesudah</TableHead>
                  <TableHead className="text-right">Selisih</TableHead>
                  <TableHead>Ref / Catatan</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-xs">{fmtDateTime(m.created_at)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{m.movement_type}</Badge></TableCell>
                    <TableCell className="text-right text-sm">{m.quantity}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{m.qty_before} → {m.qty_after}</TableCell>
                    <TableCell className={`text-right text-xs font-medium ${m.variance > 0 ? "text-emerald-700" : m.variance < 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                      {m.variance > 0 ? "+" : ""}{m.variance ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {(m.po_number || m.invoice_number || m.due_date) && (
                        <div className="flex flex-col gap-0.5 text-muted-foreground">
                          {m.po_number && <span data-testid={`mv-po-${m.id}`}>PO: {m.po_number}</span>}
                          {m.invoice_number && <span>Inv: {m.invoice_number}</span>}
                          {m.due_date && <span className="text-amber-700">Jatuh tempo: {m.due_date}</span>}
                        </div>
                      )}
                      <div>{m.note || (!m.po_number && !m.invoice_number ? "—" : "")}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {m.movement_type === "opname" && canCorrectOpname && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`opname-edit-${m.id}`}
                          onClick={() => { setMovementsOpen(false); openMoveDialog(movementItem, "opname"); }}
                          title="Koreksi hasil opname (input ulang stok fisik)"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">Belum ada movement.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item dialog */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Edit Item" : "Tambah Item"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitItem} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nama *</Label>
                <Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <Label>SKU</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
              </div>
              <div>
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cabang *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="item-branch-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs/btl/box/..." />
              </div>
              {!editing && (
                <div>
                  <Label>Stok Awal</Label>
                  <Input type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                </div>
              )}
              <div>
                <Label>Min Stok</Label>
                <Input type="number" min="0" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
              </div>
              <div>
                <Label>Harga Beli</Label>
                <Input type="number" min="0" step="1" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setItemDialogOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="item-save-btn">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Treatment recipe (BOM) dialog */}
      <Dialog open={recipeOpen} onOpenChange={setRecipeOpen}>
        <DialogContent className="max-w-2xl" data-testid="recipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Beaker className="h-5 w-5" /> Resep / Komposisi Treatment
            </DialogTitle>
            <DialogDescription>
              Tentukan bahan (BHP/obat/produk) yang otomatis terpotong dari stok setiap kali treatment ini terjual di POS.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Treatment</Label>
              <Select value={recipeTreatmentId} onValueChange={loadRecipe}>
                <SelectTrigger data-testid="recipe-treatment-select"><SelectValue placeholder="Pilih treatment" /></SelectTrigger>
                <SelectContent>
                  <div className="sticky top-0 z-10 bg-popover p-1.5">
                    <Input
                      data-testid="recipe-treatment-search"
                      placeholder="Cari nama treatment..."
                      value={recipeTreatmentQ}
                      onChange={(e) => setRecipeTreatmentQ(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="h-10 text-base md:h-9 md:text-sm"
                    />
                  </div>
                  {treatmentsList
                    .filter((t) => (t.name || "").toLowerCase().includes(recipeTreatmentQ.toLowerCase()))
                    .map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  {treatmentsList.filter((t) => (t.name || "").toLowerCase().includes(recipeTreatmentQ.toLowerCase())).length === 0 && (
                    <div className="px-2 py-2 text-xs text-muted-foreground">Tidak ada treatment cocok</div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {recipeTreatmentId && (
              <div className="space-y-2">
                {recipeItems.map((r, idx) => (
                  <div key={idx} className="flex items-end gap-2" data-testid={`recipe-row-${idx}`}>
                    <div className="flex-1">
                      <Label className="text-xs">Bahan</Label>
                      <Select value={r.inventory_id} onValueChange={(v) => updateRecipeRow(idx, { inventory_id: v })}>
                        <SelectTrigger data-testid={`recipe-item-select-${idx}`}><SelectValue placeholder="Pilih item" /></SelectTrigger>
                        <SelectContent>
                          <div className="sticky top-0 z-10 bg-popover p-1.5">
                            <Input
                              data-testid={`recipe-item-search-${idx}`}
                              placeholder="Cari nama BHP..."
                              value={r._q || ""}
                              onChange={(e) => updateRecipeRow(idx, { _q: e.target.value })}
                              onKeyDown={(e) => e.stopPropagation()}
                              className="h-10 text-base md:h-9 md:text-sm"
                            />
                          </div>
                          {items
                            .filter((i) => (i.name || "").toLowerCase().includes((r._q || "").toLowerCase()))
                            .map((i) => <SelectItem key={i.id} value={i.id}>{i.name} (stok {i.quantity} {i.unit})</SelectItem>)}
                          {items.filter((i) => (i.name || "").toLowerCase().includes((r._q || "").toLowerCase())).length === 0 && (
                            <div className="px-2 py-2 text-xs text-muted-foreground">Tidak ada BHP cocok</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Qty / treatment</Label>
                      <Input type="number" min="0" step="any" data-testid={`recipe-qty-${idx}`} value={r.quantity} onChange={(e) => updateRecipeRow(idx, { quantity: e.target.value })} />
                    </div>
                    <Button type="button" size="sm" variant="ghost" data-testid={`recipe-remove-${idx}`} onClick={() => removeRecipeRow(idx)}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" data-testid="recipe-add-row" onClick={addRecipeRow}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Tambah Bahan
                </Button>
                {recipeItems.length === 0 && <p className="text-sm text-muted-foreground">Belum ada bahan. Tambahkan bahan yang dipakai treatment ini.</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRecipeOpen(false)}>Batal</Button>
            <Button type="button" data-testid="recipe-save-btn" disabled={!recipeTreatmentId || recipeSaving} onClick={saveRecipe}>
              {recipeSaving ? "Menyimpan..." : "Simpan Resep"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock opname (selisih) dialog */}
      <Dialog open={opnameOpen} onOpenChange={setOpnameOpen}>
        <DialogContent className="max-w-2xl" data-testid="opname-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ClipboardList className="h-5 w-5" /> Stok Opname — Selisih Fisik vs Sistem
            </DialogTitle>
            <DialogDescription>
              Masukkan hasil hitung fisik. Selisih dihitung otomatis (fisik − sistem). Simpan untuk menyesuaikan stok.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Sistem</TableHead>
                  <TableHead className="text-right">Stok Fisik</TableHead>
                  <TableHead className="text-right">Selisih</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice(0, 300).map((i) => {
                  const phys = opnameRows[i.id];
                  const sel = phys === "" || phys == null ? null : Number(phys) - (i.quantity || 0);
                  return (
                    <TableRow key={i.id} data-testid={`opname-row-${i.id}`}>
                      <TableCell className="text-sm font-medium">{i.name}</TableCell>
                      <TableCell className="text-right text-sm">{i.quantity} {i.unit}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number" min="0" step="1"
                          data-testid={`opname-input-${i.id}`}
                          className="w-24 ml-auto text-right"
                          value={phys ?? ""}
                          onChange={(e) => setOpnameRows({ ...opnameRows, [i.id]: e.target.value })}
                        />
                      </TableCell>
                      <TableCell className={`text-right text-sm font-medium ${sel == null ? "text-muted-foreground" : sel > 0 ? "text-emerald-700" : sel < 0 ? "text-rose-700" : ""}`} data-testid={`opname-selisih-${i.id}`}>
                        {sel == null ? "—" : (sel > 0 ? `+${sel}` : sel)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpnameOpen(false)}>Batal</Button>
            <Button type="button" data-testid="opname-save-btn" disabled={opnameSaving} onClick={saveOpname}>
              {opnameSaving ? "Menyimpan..." : "Simpan Opname"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
